import { subDays } from "date-fns";
import { useSearchParams } from "react-router-dom";

import { NOTIFICATION_DEFAULT_WINDOW_DAYS, NOTIFICATION_FILTER_PARAMS } from "../constants";
import { notificationSeveritySchema, notificationStatusSchema } from "../schemas";
import type { NotificationFilters } from "../types";
import { parseNotificationStatus } from "../utils";

import { URL_PARAMS } from "@/constants/urlParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { formatLocalDateOnly, parseLocalDateOnly } from "@/utils/dateUtils";

export function useNotificationPageFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
  const status = parseNotificationStatus(searchParams.get(NOTIFICATION_FILTER_PARAMS.STATUS));
  const today = new Date();
  const isWindowed = status !== notificationStatusSchema.enum.unread;
  const dateParam = (key: string, fallback: Date) => {
    const date = parseLocalDateOnly(searchParams.get(key)) ?? (isWindowed ? fallback : undefined);
    return date && formatLocalDateOnly(date);
  };

  const filters: NotificationFilters = {
    status,
    search: debouncedSearchValue,
    dateFrom: dateParam(URL_PARAMS.DATE_FROM, subDays(today, NOTIFICATION_DEFAULT_WINDOW_DAYS)),
    dateTo: dateParam(URL_PARAMS.DATE_TO, today),
    severities: searchParams.getAll(NOTIFICATION_FILTER_PARAMS.SEVERITY).flatMap((value) => {
      const severity = notificationSeveritySchema.safeParse(value);
      return severity.success ? [severity.data] : [];
    }),
    actorIds: searchParams.getAll(NOTIFICATION_FILTER_PARAMS.ACTOR_ID),
    clientIds: searchParams.getAll(NOTIFICATION_FILTER_PARAMS.CLIENT_ID),
  };

  return { filters, searchValue, setSearchValue, searchParams, setSearchParams };
}
